import { useCallback, useMemo } from 'react'
import * as Yup from 'yup'
import { isRequired } from '@cbgms/base/utils/validations'
import { IWholesalerCatalog, VehicleSource, WholesaleCatalogType } from '@cbgms/api'
import { createWholesalerCatalog, editWholesalerCatalog } from 'app-support/wholesale/state/actions'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'
import { IFormValues } from './types'
import { CatalogFieldsCarsys } from './CatalogFieldsCarsys'
import { CatalogFieldsEMDM } from './CatalogFieldsEMDM'
import { CatalogFieldsVPOM } from './CatalogFieldsVPOM'
import { CatalogFieldsTecDoc } from './CatalogFieldsTecDoc'
import { CatalogFieldsHbase } from './CatalogFieldsHbase'
import { BasicFormLayout, Dropdown, Form, NoPaperComponent } from '@carsys/form'
import { useNotifier } from '@cbgms/base/notifier'
import { OnSubmit, OnSubmitSuccess } from '@carsys/form/src/types'
import { Box, Divider, Typography } from '@mui/material'
import { useQueryClient } from '@cbgms/base/http/use-query'
import { wholesalerCatalogsCacheKey } from 'app-support/wholesale/hooks/cache-keys'
import { CatalogFieldsVehicleSource } from 'app-support/wholesale/components/catalog/CatalogFieldsVehicleSource'
import { CountryCode } from '@carsys/enums/country-code'
import { CatalogFieldsQuickNavigateToProductGroup } from './CatalogFieldsQuickNavigateToProductGroup'
import { DashboardCatalogAndExternalResources } from './DashboardCatalogAndExternalResources'

const defaultFormValues: IFormValues = {
  wholesaleCatalogType: WholesaleCatalogType.VPOM,
  CarsysProductTreeUUID: '',
  // Vehicle Settings:
  VehiclePrimaryVehicleSource: VehicleSource.Unconfigured,
  VehicleSources: [],
  // VPOM Settings
  VPOMProductTreeID: '',
  VPOMDashboardCatalogUUIDS: [],
  DashboardCatalogInfo: {
    ExternalPortals: [],
    Groups: [],
    OrderInPreparation: [],
    AddToOrderMenu: [],
    QuickNavigationProductGroup: []
  },
  VPOMUseDevelopmentAPI: false,
  VPOMHidePDHForNonTyres: false,
  RemoteIDOverride: '',
  ExhaustGroupPlaceholder: '',
  SteeringGroupPlaceholder: '',
  // HbaseSettings:
  HbaseOverrideSupplierID: '',
  HbaseOverrideAPIKey: '',
  HbaseOverrideMGAppID: '',
  HbaseOverrideMGAppSecret: '',
  HbaseMapSurchargeFromCatalog: true,
  // MDM Settings:
  MDMOverrideApplicationID: '',
  MDMOverrideUsername: '',
  MDMOverridePassword: '',

  // TecDoc Settings:
  TecDocOverrideProvider: '',
  TecDocOverrideAPIKey: '',

  // Quick navigate to productgroup:
  QuickNavToProductGroupUUIDS: []
}

const options = [
  { text: WholesaleCatalogType.CARSYS_PRODUCT_TREE, value: WholesaleCatalogType.CARSYS_PRODUCT_TREE },
  { text: WholesaleCatalogType.EMDM, value: WholesaleCatalogType.EMDM },
  { text: WholesaleCatalogType.HBASE, value: WholesaleCatalogType.HBASE },
  { text: WholesaleCatalogType.VPOM, value: WholesaleCatalogType.VPOM },
  { text: WholesaleCatalogType.WholesalerGenArt, value: WholesaleCatalogType.WholesalerGenArt },
  { text: WholesaleCatalogType.TecDoc, value: WholesaleCatalogType.TecDoc }
]

export interface LinkCatalogToWholesalerProps {
  onSubmit: OnSubmit<IFormValues, any>
  onSubmitSuccess: OnSubmitSuccess<IFormValues, any>
  initialValues: IFormValues
}
const LinkCatalogToWholesalerForm = ({ onSubmit, initialValues, onSubmitSuccess }: LinkCatalogToWholesalerProps) => {
  const { t } = useTranslation()
  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        wholesaleCatalogType: Yup.string().required(),
        VehicleSources: Yup.array().of(
          Yup.object().shape({
            Source: Yup.string().required(),
            Target: Yup.string().required(),
            Country: Yup.string().required().notOneOf([CountryCode.ZZ])
          })
        )
      }),
    []
  )

  return (
    <Form
      // NavigationConfirmation should stay off until LinkCatalogToWholesaler is refactored.
      // Having multiple navigationPrompt(s) on the same page is causing issues.
      NavigationConfirmation={null}
      initialValues={initialValues}
      onSubmit={onSubmit}
      onSubmitSuccess={onSubmitSuccess}
      validationSchema={validationSchema}
    >
      <BasicFormLayout
        title={<Typography variant='h4'>{t('wholesaler-catalog.config-vehicle-sources')}</Typography>}
        PaperComponent={NoPaperComponent}
      >
        <Box data-testid='form-add-link-catalog' mt={1}>
          <Box>
            <CatalogFieldsVehicleSource />
          </Box>
          <Divider sx={{ marginTop: 2, marginBottom: 2 }} />
          <Typography variant='h4' sx={{ mb: 1 }}>
            {t('wholesaler-catalog.label')}
          </Typography>
          <Box sx={{ '&>*': { marginTop: 1 } }}>
            <Dropdown
              label={t('wholesaler-catalog.catalog-dropdown-label')}
              name='wholesaleCatalogType'
              required
              validate={isRequired}
              options={options}
            />
            <CatalogFieldsCarsys />
            <CatalogFieldsVPOM />
            <CatalogFieldsHbase />
            <CatalogFieldsEMDM />
            <CatalogFieldsTecDoc />
            <DashboardCatalogAndExternalResources />
          </Box>
          <Box mb={2}>
            <CatalogFieldsQuickNavigateToProductGroup />
          </Box>
        </Box>
      </BasicFormLayout>
    </Form>
  )
}

interface IProps {
  onLink?: () => void
}
export const NewLinkCatalogToWholesaler = ({ onLink }: IProps) => {
  const { wholesaleUUID } = useParams<{ wholesaleUUID: string }>()
  const dispatch = useDispatch()
  const notifier = useNotifier()
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const initialValues = defaultFormValues
  const onSubmit = useCallback(async (formValues: IFormValues) => {
    await dispatch(
      createWholesalerCatalog(wholesaleUUID, {
        ...formValues,
        WholesalerUUID: wholesaleUUID,
        CatalogType: formValues.wholesaleCatalogType,
        CarsysProductTreeUUID: formValues.CarsysProductTreeUUID || '',
        VPOMProductTreeID: formValues.VPOMProductTreeID || '',
        VPOMDashboardCatalogUUIDS: formValues.VPOMDashboardCatalogUUIDS || [],
        DashboardCatalogInfo: formValues.DashboardCatalogInfo || {
          ExternalPortals: [],
          Groups: [],
          OrderInPreparation: [],
          AddToOrderMenu: [],
          QuickNavigationProductGroup: []
        },
        QuickNavToProductGroupUUIDS: formValues.QuickNavToProductGroupUUIDS || []
      })
    )
    queryClient.invalidateQueries(wholesalerCatalogsCacheKey(wholesaleUUID))
  }, [])
  const onSubmitSuccess = useCallback(() => {
    notifier.success(t('wholesaler-catalog.create-success'))
    onLink?.()
  }, [onLink, t])

  return <LinkCatalogToWholesalerForm onSubmit={onSubmit} onSubmitSuccess={onSubmitSuccess} initialValues={initialValues} />
}

interface IEditProps extends IProps {
  cat: IWholesalerCatalog
}
export const EditLinkCatalogToWholesaler = ({ onLink, cat }: IEditProps) => {
  const { wholesaleUUID } = useParams<{ wholesaleUUID: string }>()
  const dispatch = useDispatch()
  const notifier = useNotifier()
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const initialValues = useMemo<IFormValues>(() => ({ ...defaultFormValues, ...cat, wholesaleCatalogType: cat.CatalogType }), [cat])
  const onSubmit = useCallback(async (formValues: IFormValues) => {
    await dispatch(
      editWholesalerCatalog(wholesaleUUID, cat.UUID, {
        ...formValues,
        WholesalerUUID: wholesaleUUID,
        CatalogType: formValues.wholesaleCatalogType,
        CarsysProductTreeUUID: formValues.CarsysProductTreeUUID || '',
        VPOMProductTreeID: formValues.VPOMProductTreeID || '',
        VPOMDashboardCatalogUUIDS: formValues.VPOMDashboardCatalogUUIDS || [],
        DashboardCatalogInfo: formValues.DashboardCatalogInfo || {
          ExternalPortals: [],
          Groups: [],
          OrderInPreparation: [],
          AddToOrderMenu: [],
          QuickNavigationProductGroup: []
        },
        ExhaustGroupPlaceholder: formValues.ExhaustGroupPlaceholder || '',
        SteeringGroupPlaceholder: formValues.SteeringGroupPlaceholder || ''
      })
    )
    queryClient.invalidateQueries(wholesalerCatalogsCacheKey(wholesaleUUID))
  }, [])
  const onSubmitSuccess = useCallback(() => {
    notifier.success(t('wholesaler-catalog.edit-success'))
    onLink?.()
  }, [onLink, t])

  return <LinkCatalogToWholesalerForm onSubmit={onSubmit} onSubmitSuccess={onSubmitSuccess} initialValues={initialValues} />
}
